import { useRef, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface TheLabProps {
  onOpenProject: (id: string) => void;
  onOpenList: () => void;
}

// RevealTitle built strictly for GSAP manipulation matching identical TheLab layout
function RevealTitle({
  children,
  className,
  heightClass = "h-[0.85em]",
  index = 0,
}: {
  children: React.ReactNode;
  className?: string;
  heightClass?: string;
  index?: number;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden flex flex-col w-full text-left",
        heightClass,
      )}
    >
      <span
        className={cn("title-reveal-text relative block shrink-0", className)}
      >
        {children}
      </span>
      <span
        className={cn(
          "title-reveal-text absolute top-full left-0 block w-full shrink-0",
          className,
        )}
        aria-hidden
      >
        {children}
      </span>
    </div>
  );
}

// BentoCard built exactly to overlay magnetic effects without altering intrinsic Flex/Tailwind DOM flow
function BentoCard({
  children,
  className,
  bgClass,
  onClick,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  bgClass?: string;
  onClick?: () => void;
  id: string;
}) {
  return (
    <div
      className={cn(
        "bento-card relative overflow-hidden group cursor-pointer rounded-3xl transition-transform duration-300 hover:scale-y-[1.03]",
        bgClass,
        className,
      )}
      onClick={onClick}
      data-hovered="false"
      id={id}
    >
      <svg className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen z-0">
        <defs>
          <linearGradient
            id={`shimmer-bg-${id}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#f43f5e" stopOpacity="0.8" />
            <stop offset="33%" stopColor="#818cf8" stopOpacity="1" />
            <stop offset="66%" stopColor="#2dd4bf" stopOpacity="1" />
            <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        <rect
          className="border-trace-rect drop-shadow-[0_0_12px_rgba(129,140,248,0.8)] opacity-0"
          x="0"
          y="0"
          width="100%"
          height="100%"
          rx="24"
          fill="none"
          stroke={`url(#shimmer-bg-${id})`}
          strokeWidth="4"
        />
      </svg>
      {children}
    </div>
  );
}

export function TheLab({ onOpenProject, onOpenList }: TheLabProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const validCards = gsap.utils.toArray(".bento-card", container);
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container,
          start: "top 65%",
        },
      });

      // Entry Animation: Tactical HUD with double-staggered pause implementation
      gsap.set(validCards, { opacity: 0, scale: 0.9, y: 60 });

      tl.to(validCards, {
        opacity: 1,
        scale: 1,
        y: -15,
        duration: 1.0,
        stagger: 0.15,
        ease: "power2.out",
      }).to(
        validCards,
        {
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: "back.out(2)",
          onComplete: () => startIdleSequence(validCards as Element[]),
        },
        "+=0.2",
      );

      // Custom Hover logic (GSAP Magnetic + Text slide + Color preservation via tailwind in markup)
      validCards.forEach((cardEl) => {
        const card = cardEl as HTMLElement;
        const titleTexts = card.querySelectorAll(".title-reveal-text");
        const manipulableChildren = Array.from(card.children).filter(
          (child) => child.tagName.toLowerCase() !== "svg",
        );

        card.addEventListener("mouseenter", () => {
          card.setAttribute("data-hovered", "true");
          gsap.to(titleTexts, {
            yPercent: -100,
            duration: 0.4,
            stagger: 0.05,
            ease: "power3.out",
            overwrite: "auto",
          });
        });

        card.addEventListener("mousemove", (e: MouseEvent) => {
          const rect = card.getBoundingClientRect();
          const x = e.clientX - rect.left - rect.width / 2;
          const y = e.clientY - rect.top - rect.height / 2;

          gsap.to(manipulableChildren, {
            x: x * 0.05,
            y: y * 0.05,
            duration: 0.5,
            ease: "power2.out",
            overwrite: "auto",
          });
        });

        card.addEventListener("mouseleave", () => {
          card.setAttribute("data-hovered", "false");
          gsap.to(titleTexts, {
            yPercent: 0,
            duration: 0.4,
            stagger: 0.05,
            ease: "power3.out",
            overwrite: "auto",
          });

          gsap.to(manipulableChildren, {
            x: 0,
            y: 0,
            duration: 0.5,
            ease: "power2.out",
            overwrite: "auto",
          });
        });
      });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  const startIdleSequence = (validCards: Element[]) => {
    let currentIndex = 0;

    const playNext = () => {
      if (!validCards[0] || !document.body.contains(validCards[0])) return;

      const card = validCards[currentIndex];
      currentIndex = (currentIndex + 1) % validCards.length;

      if (!card || card.getAttribute("data-hovered") === "true") {
        setTimeout(playNext, 200);
        return;
      }

      const rect = card.querySelector(".border-trace-rect") as SVGRectElement;
      const titleTexts = card.querySelectorAll(".title-reveal-text");

      if (!rect) {
        setTimeout(playNext, 200);
        return;
      }

      // True Geometry for perimeter looping logic
      const w = card.clientWidth;
      const h = card.clientHeight;
      const rx = 24;
      const pathLength = 2 * w + 2 * h - 8 * rx + 2 * Math.PI * rx;

      const tlIdle = gsap.timeline({
        onComplete: () => {
          setTimeout(playNext, 500);
        },
      });

      tlIdle.set(rect, {
        strokeDasharray: `300 ${pathLength * 2}`,
        strokeDashoffset: 0,
        opacity: 0,
      });

      tlIdle
        .to(rect, { opacity: 1, duration: 0.4 })
        .to(
          rect,
          {
            strokeDashoffset: -pathLength,
            duration: 2.5,
            ease: "power2.inOut",
          },
          "-=0.2",
        )
        .add(() => {
          if (
            titleTexts.length &&
            card.getAttribute("data-hovered") !== "true"
          ) {
            // Slide text up sequentially
            gsap.fromTo(
              titleTexts,
              { yPercent: 0 },
              {
                yPercent: -100,
                duration: 0.6,
                stagger: 0.05,
                ease: "power3.inOut",
              },
            );

            // Revert afterwards to preserve looping visibility
            gsap.to(titleTexts, {
              yPercent: 0,
              duration: 0.6,
              stagger: 0.05,
              ease: "power3.inOut",
              delay: 1.5,
            });
          }
        }, "-=1.8")
        .to(rect, { opacity: 0, duration: 0.5 }, "-=0.5");
    };

    setTimeout(playNext, 1000);
  };

  return (
    <section
      className="relative bg-background py-32 z-20 min-h-screen"
      data-section="projects"
    >
      <div className="container mx-auto px-6">
        <div className="mb-16 md:mb-20">
          <div className="text-base text-muted-foreground font-mono uppercase tracking-[0.2em] border-l-2 border-blue-500 pl-4 text-left">
            02 / The Lab
          </div>
        </div>

        {/* Parallax Container matched to original sizing */}
        <div
          ref={containerRef}
          className="w-full mx-auto relative flex flex-col md:h-[min(90vh,960px)] h-full"
        >
          {/* TOP SECTION (50%) */}
          <BentoCard
            id="bento-1"
            className="h-2/5 w-full"
            bgClass="bg-muted-foreground"
            onClick={() => onOpenProject("knot-wallet")}
          >
            <div className="relative z-10 h-full flex flex-col justify-between p-8 py-6">
              <div className="absolute top-6 right-8 text-background opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-y-2 group-hover:translate-y-0">
                <ArrowUpRight className="w-8 h-8" />
              </div>

              <div className="mb-3">
                <h3 className="text-5xl md:text-6xl lg:text-7xl font-bold text-background leading-[0.85] tracking-tighter uppercase whitespace-pre-line">
                  <RevealTitle heightClass="h-[0.85em]" index={0}>
                    The Knot
                  </RevealTitle>
                  <RevealTitle heightClass="h-[0.85em]" index={1}>
                    Wallet
                  </RevealTitle>
                </h3>
              </div>

              <div className="w-full">
                <div className="w-full h-px bg-background/20 mb-3" />
                <div className="flex justify-between items-end">
                  <div className="flex flex-col gap-1 text-background bg-accent/20">
                    <div className="px-3 py-1 w-fit backdrop-blur-sm transition-colors duration-300 bg-blue-600 text-white">
                      <span className="text-sm md:text-base font-mono uppercase tracking-wider">
                        Featured Work
                      </span>
                    </div>
                  </div>
                  <div className="text-right max-w-sm">
                    <p className="text-sm md:text-base lg:text-lg text-secondary font-medium leading-relaxed">
                      Chain Abstracted wallet, using intents and a dispatch +
                      accumulate pattern.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </BentoCard>

          {/* BOTTOM SECTION (50%) */}
          <div className="h-3/5 w-full pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex flex-col gap-4 h-full">
              {/* X402 Library */}
              <BentoCard
                id="bento-2"
                className="relative flex-1 p-6 flex flex-col justify-end hover:bg-secondary/80"
                bgClass="bg-secondary"
                onClick={() => onOpenProject("x402-client")}
              >
                <div className="w-8 h-8 absolute right-4 top-4 rounded-full border border-blue-500 flex items-center justify-center bg-background/5">
                  <span className="text-[10px] font-mono text-secondary-foreground/70">
                    01
                  </span>
                </div>
                <div className="relative z-10 w-full mt-auto">
                  <h4 className="text-2xl md:text-[1.4rem] lg:text-3xl xl:text-4xl font-bold text-secondary-foreground mb-2 mt-4 leading-[0.85] uppercase tracking-tight space-y-2">
                    <RevealTitle heightClass="h-[0.85em]" index={0}>
                      X402
                    </RevealTitle>
                    <RevealTitle heightClass="h-[0.85em]" index={1}>
                      Client Library
                    </RevealTitle>
                  </h4>
                  <p className="text-sm lg:text-base text-secondary-foreground/70 leading-relaxed">
                    Lightweight library that helps flutter apps manage paid APIs
                    lifecycle easily.
                  </p>
                </div>
              </BentoCard>

              {/* Ledger DMK */}
              <BentoCard
                id="bento-3"
                className="flex-1 p-6 flex flex-col justify-between hover:border-border"
                bgClass="bg-background border border-muted-foreground/40"
                onClick={() => onOpenProject("tiny-vector-db")}
              >
                <div className="w-fit">
                  <div className="px-2 py-1 border border-border bg-muted/20 backdrop-blur-sm">
                    <span className="text-sm font-mono uppercase tracking-wider text-foreground">
                      Hackathon
                    </span>
                  </div>
                </div>
                <div className="relative z-10 mt-auto">
                  <h4 className="text-xl font-bold text-foreground mb-2 leading-[0.85em] tracking-tight">
                    <RevealTitle heightClass="h-[0.9em]" index={0}>
                      Ledger DMK
                    </RevealTitle>
                  </h4>
                  <p className="text-sm lg:text-base text-muted-foreground leading-relaxed">
                    A Cosmos DMK Signer for Ledger devices
                  </p>
                </div>
              </BentoCard>
            </div>

            {/* Middle: ECDSA Signing */}
            <BentoCard
              id="bento-4"
              className="h-full p-6 flex flex-col justify-between hover:bg-accent/30"
              bgClass="bg-accent/20"
              onClick={() => onOpenProject("web3-signers")}
            >
              <div className="absolute top-6 right-6 text-sm lg:text-base font-mono uppercase tracking-widest text-muted-foreground/70 z-10">
                flutter package
              </div>
              <div className="mt-8 md:mt-auto relative z-10">
                <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-6 uppercase leading-[0.9] tracking-tight space-y-1">
                  <RevealTitle heightClass="h-[0.9em]" index={0}>
                    ECDSA Signing
                  </RevealTitle>
                  <RevealTitle heightClass="h-[0.9em]" index={1}>
                    Library
                  </RevealTitle>
                </h3>
                <div className="w-12 h-px bg-blue-500 mb-4" />
                <p className="text-sm lg:text-base text-muted-foreground leading-relaxed max-w-[90%]">
                  A generic signing interface for smart accounts validation and
                  EOAs
                </p>
              </div>
            </BentoCard>

            <div className="flex flex-col gap-4 h-full">
              {/* AA SDK */}
              <BentoCard
                id="bento-5"
                className="flex-1 p-6 flex flex-col justify-between hover:bg-primary/90"
                bgClass="bg-primary"
                onClick={() => onOpenProject("account-abstraction-sdk")}
              >
                <div className="flex justify-between items-start text-primary-foreground/60 w-full mb-4">
                  <div className="border border-blue-500 p-2 w-10 h-10 flex items-center justify-center">
                    <span className="text-sm font-mono">03</span>
                  </div>
                  <span className="text-sm font-mono uppercase tracking-widest">
                    Developer tool
                  </span>
                </div>
                <div className="relative z-10 mt-auto">
                  <h3 className="text-xl font-bold text-primary-foreground mb-3 leading-[0.85]">
                    <RevealTitle heightClass="h-[0.85em]">
                      Account Abstraction SDK
                    </RevealTitle>
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-px bg-primary-foreground/40 shrink-0" />
                    <p className="text-sm lg:text-base text-primary-foreground/70 leading-relaxed font-medium h-24 md:h-min">
                      Frameworks for building with ERC-4337 & ERC-7702 in
                      flutter apps
                    </p>
                  </div>
                </div>
              </BentoCard>

              {/* Tiny Vector DB */}
              <BentoCard
                id="bento-6"
                className="flex-1 p-6 flex flex-col justify-center items-start text-start hover:bg-muted/60"
                bgClass="bg-muted/50"
                onClick={() => onOpenProject("eip-7702")}
              >
                <div className="relative z-10 w-full h-full flex flex-col justify-center">
                  <span className="text-sm lg:text-base font-mono uppercase tracking-widest text-muted-foreground/80 mb-2">
                    Origin - Payments
                  </span>
                  <h4 className="text-lg lg:text-xl font-bold text-foreground leading-[0.85]">
                    <RevealTitle heightClass="h-[0.85em]">
                      Host card emulation & NFC
                    </RevealTitle>
                  </h4>
                </div>
              </BentoCard>
            </div>
          </div>

          <div className="w-full mt-4 flex justify-end transition-opacity duration-500 hover:opacity-100 opacity-80">
            <button
              onClick={onOpenList}
              className="group flex items-center gap-2 text-blue-500 hover:text-blue-600 transition-colors py-2"
            >
              <span className="text-base font-mono uppercase tracking-widest">
                View Full List
              </span>
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
